import moment from "moment";
import { globals, page as pageIndex } from "../../globals";
import {
  EXTRACTOR_FIELD,
  EXTRACTOR_FIELDS,
} from "../../routes/RegsInjector/constants";
import { logOut } from "./auth";
import { showModal } from "./modal";
import { UPDATE_DATA, actionHandler } from "./updateData";

// Date fields (Issued Date, Comment Deadline, Meeting Date) are forced to a
// YYYY-MM-DD string wherever a value exists — both on load and on edit.
const DATE_FIELD_IDS = new Set(
  EXTRACTOR_FIELDS.filter((field) => field.isDate).map((field) => field.id)
);

// Formats the Extractor/staff may supply; parsed leniently and re-emitted as
// YYYY-MM-DD. An unparseable value is left untouched so staff can correct it.
const DATE_INPUT_FORMATS = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "MM/DD/YYYY",
  "M/D/YYYY",
  "MM-DD-YYYY",
  "MMMM D, YYYY",
  "MMM D, YYYY",
  "D MMMM YYYY",
];

function toIsoDate(value) {
  if (!value) {
    return value;
  }
  const parsed = moment(String(value).trim(), DATE_INPUT_FORMATS, false);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
}

// Read a property regardless of JSON casing (a .NET Core API may serialize
// PascalCase C# properties as camelCase, e.g. RegsId -> regsId).
function readProp(obj, name) {
  if (!obj) {
    return undefined;
  }
  const key = Object.keys(obj).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  return key ? obj[key] : undefined;
}

// The RegsExtractorFields live nested under Parse.Data (mirrored in
// Parse.Fields), not at the response root.
function parseFields(payload) {
  return (
    (payload && payload.Parse && (payload.Parse.Data || payload.Parse.Fields)) ||
    {}
  );
}

// Find the source page URL anywhere in the extractor payload. Its exact
// location/casing isn't known (it may sit under Parse, Parse.Data, or the
// root, and be named url/Url/SourceUrl/PageUrl), so search recursively and
// prefer the most specific url-like key: exact "url" > "*url" > "*url*".
function findSourceUrl(payload) {
  let best;
  const rankKey = (key) => {
    const k = key.toLowerCase();
    if (k === "url") return 3;
    if (k.endsWith("url")) return 2;
    if (k.includes("url")) return 1;
    return 0;
  };
  const visit = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (typeof value === "string" && value.trim()) {
        const rank = rankKey(key);
        if (rank > 0 && (!best || rank > best.rank)) {
          best = { rank, value: value.trim() };
        }
      } else if (value && typeof value === "object") {
        visit(value);
      }
    });
  };
  visit(payload);
  return best ? best.value : "";
}

// Re-write a single date field in state to YYYY-MM-DD if it isn't already.
function normalizeDateField(state, id) {
  if (!DATE_FIELD_IDS.has(id)) {
    return state;
  }
  const textValues = state.formDataValue.RINJ[EXTRACTOR_FIELD].textValues;
  const formatted = toIsoDate(textValues[id]);
  if (formatted === textValues[id]) {
    return state;
  }
  return {
    ...state,
    formDataValue: {
      ...state.formDataValue,
      RINJ: {
        ...state.formDataValue.RINJ,
        [EXTRACTOR_FIELD]: {
          ...state.formDataValue.RINJ[EXTRACTOR_FIELD],
          textValues: { ...textValues, [id]: formatted },
        },
      },
    },
  };
}

// ------------------------------------
// Constants
// ------------------------------------
export const RETURN_REGSINJECTOR_INITIAL_STATE =
  "RETURN_REGSINJECTOR_INITIAL_STATE";

export const GET_REGSEXTRACTOR_START = "GET_REGSEXTRACTOR_START";
export const GET_REGSEXTRACTOR_SUCCESS = "GET_REGSEXTRACTOR_SUCCESS";
export const GET_REGSEXTRACTOR_FAILURE = "GET_REGSEXTRACTOR_FAILURE";

export const SAVE_REGSINJECTOR_START = "SAVE_REGSINJECTOR_START";
export const SAVE_REGSINJECTOR_SUCCESS = "SAVE_REGSINJECTOR_SUCCESS";
export const SAVE_REGSINJECTOR_FAILURE = "SAVE_REGSINJECTOR_FAILURE";

export const TOGGLE_REGSINJECTOR_TEAM = "TOGGLE_REGSINJECTOR_TEAM";

export const GET_REGSMATCH_START = "GET_REGSMATCH_START";
export const GET_REGSMATCH_SUCCESS = "GET_REGSMATCH_SUCCESS";
export const GET_REGSMATCH_FAILURE = "GET_REGSMATCH_FAILURE";

// POST target for saving staff-reviewed fields back.
const SAVE_ENDPOINT = `${globals.apiUrl}/regsextractor/save`;

// ------------------------------------
// Action Creators
// ------------------------------------
export function returnInitialState() {
  return {
    type: RETURN_REGSINJECTOR_INITIAL_STATE,
  };
}

// Toggle a Team Group number (1-4) in/out of the selected set. Not mutually
// exclusive; the selection is saved as a comma-delimited "Team" value.
export function toggleTeam(team) {
  return { type: TOGGLE_REGSINJECTOR_TEAM, team: String(team) };
}

// Fetch the RegsExtractorFields payload the StateScape Extractor stored under
// `guid` and load it into the form. Called on page entry with ?guid=... .
export function getExtractorFields(guid) {
  return (dispatch) => {
    dispatch({ type: GET_REGSEXTRACTOR_START });

    const options = {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    };

    return fetch(
      `${globals.apiUrl}/regsextractor?guid=${encodeURIComponent(guid)}`,
      options
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 401) {
          dispatch(logOut());
        } else if (response.status === 400) {
          response.text().then((text) => {
            if (text) {
              dispatch({ type: GET_REGSEXTRACTOR_FAILURE, json: text });
              dispatch(showModal("Error"));
            }
          });
        }
      })
      .then((json) => {
        if (json) {
          dispatch({ type: GET_REGSEXTRACTOR_SUCCESS, payload: { json } });
          // Look for existing regulations that may duplicate this extract.
          // The endpoint 400s if both are blank, so only ask when we have one.
          const title = readProp(parseFields(json), "Title") || "";
          const url = findSourceUrl(json);
          if (title || url) {
            dispatch(getDuplicateMatches(title, url));
          }
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch({ type: GET_REGSEXTRACTOR_FAILURE, json: err.message });
      });
  };
}

// Ask the API for existing Reg ids that may duplicate this extract, matched on
// title and/or url. Returns a bare array of ids (newest first); [] means no
// matches. Failures are logged and treated as "no duplicates" — this is an
// advisory check, so it must never block the review with an error modal.
export function getDuplicateMatches(title, url) {
  return (dispatch) => {
    dispatch({ type: GET_REGSMATCH_START });

    const options = {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ title, url }),
    };

    return fetch(`${globals.apiUrl}/RegsExtractor/match`, options)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        if (response.status === 401) {
          dispatch(logOut());
          return undefined;
        }
        // Error responses carry a plain-text message, not JSON.
        return response.text().then((text) => {
          dispatch({ type: GET_REGSMATCH_FAILURE, json: text });
          console.log(`RegsExtractor/match ${response.status}: ${text}`);
          return undefined;
        });
      })
      .then((ids) => {
        if (Array.isArray(ids)) {
          dispatch({ type: GET_REGSMATCH_SUCCESS, payload: { ids } });
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch({ type: GET_REGSMATCH_FAILURE, json: err.message });
      });
  };
}

// Persist the staff-reviewed form values. `fields` is the assembled map of
// RegsExtractorFields (text inputs plus the State dropdown selection); `guid`
// identifies the original extracted record. Sent as a JSON POST.
export function saveModifiedFields(guid, fields) {
  return (dispatch) => {
    dispatch({ type: SAVE_REGSINJECTOR_START });

    const options = {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ guid, fields }),
    };

    return fetch(SAVE_ENDPOINT, options)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else if (response.status === 401) {
          dispatch(logOut());
        } else if (response.status === 400) {
          response.text().then((text) => {
            if (text) {
              dispatch({ type: SAVE_REGSINJECTOR_FAILURE, json: text });
              dispatch(showModal("Error"));
            }
          });
        }
      })
      .then((json) => {
        if (json) {
          dispatch({ type: SAVE_REGSINJECTOR_SUCCESS, payload: { json } });
          // On a successful save the API returns the created/updated record's
          // RegsId; send the user to that record in RegsTrackInternal. Read it
          // case-insensitively so camelCase JSON (regsId) also navigates.
          const regsId = readProp(json, "RegsId");
          if (regsId) {
            window.location.href =
              `${window.origin}/regstrackinternal?RegsId=${regsId}`;
          }
        }
      })
      .catch((err) => {
        console.log(err);
        dispatch({ type: SAVE_REGSINJECTOR_FAILURE, json: err.message });
      });
  };
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  // Field edits from the shared InputField/TextArea land here.
  // Gated on the RINJ page code so this slice ignores other pages' UPDATE_DATA.
  [UPDATE_DATA]: (state, action) => {
    if (action.location.page !== pageIndex.regsInjector) {
      return state;
    }
    // action.payload.value is the edited field id; normalize it if it's a date.
    return normalizeDateField(actionHandler(state, action), action.payload.value);
  },
  [RETURN_REGSINJECTOR_INITIAL_STATE]: () => {
    return {
      ...initialState,
    };
  },
  [TOGGLE_REGSINJECTOR_TEAM]: (state, action) => {
    const fields = state.formDataValue.RINJ[EXTRACTOR_FIELD];
    const teams = fields.teams.includes(action.team)
      ? fields.teams.filter((team) => team !== action.team)
      : [...fields.teams, action.team].sort((a, b) => Number(a) - Number(b));
    return {
      ...state,
      formDataValue: {
        ...state.formDataValue,
        RINJ: {
          ...state.formDataValue.RINJ,
          [EXTRACTOR_FIELD]: { ...fields, teams },
        },
      },
    };
  },
  [GET_REGSEXTRACTOR_START]: (state) => {
    return {
      ...state,
      isFetchingData: true,
    };
  },
  [GET_REGSEXTRACTOR_SUCCESS]: (state, action) => {
    const payload = action.payload.json;
    // The RegsExtractorFields live nested under Parse.Data (mirrored in
    // Parse.Fields), not at the response root. Map those PascalCase properties
    // into textValues, defaulting any missing/null property to "" so the inputs
    // stay controlled.
    const fields = parseFields(payload);
    const textValues = EXTRACTOR_FIELDS.reduce((values, field) => {
      const raw = fields[field.id] != null ? fields[field.id] : "";
      values[field.id] = field.isDate ? toIsoDate(raw) : raw;
      return values;
    }, {});
    // Team Group arrives (if present) as a comma-delimited "Team" string.
    const teams = fields.Team
      ? String(fields.Team)
          .split(",")
          .map((team) => team.trim())
          .filter(Boolean)
      : [];
    // Source URL of the extracted page. Not an editable field, but carried
    // through so it can be posted back on save. Located by searching the whole
    // payload since its exact key/nesting isn't guaranteed.
    const url = findSourceUrl(payload);
    return {
      ...state,
      isFetchingData: false,
      formDataValue: {
        ...state.formDataValue,
        RINJ: {
          ...state.formDataValue.RINJ,
          [EXTRACTOR_FIELD]: {
            ...state.formDataValue.RINJ[EXTRACTOR_FIELD],
            textValues,
            teams,
            url,
            // State is a dropdown (select path -> radio[id]); seed its selection
            // from the extracted value so the loaded record shows the region.
            radio: {
              ...state.formDataValue.RINJ[EXTRACTOR_FIELD].radio,
              State: fields.State != null ? fields.State : "",
            },
          },
        },
      },
    };
  },
  [GET_REGSEXTRACTOR_FAILURE]: (state, action) => {
    return {
      ...state,
      errorCode: action.json,
      isFetchingData: false,
      isError: true,
    };
  },
  [GET_REGSMATCH_START]: (state) => {
    return {
      ...state,
      isMatchingData: true,
    };
  },
  [GET_REGSMATCH_SUCCESS]: (state, action) => {
    return {
      ...state,
      isMatchingData: false,
      duplicateIds: action.payload.ids,
    };
  },
  [GET_REGSMATCH_FAILURE]: (state) => {
    // Advisory check — on failure just show nothing.
    return {
      ...state,
      isMatchingData: false,
      duplicateIds: [],
    };
  },
  [SAVE_REGSINJECTOR_START]: (state) => {
    return {
      ...state,
      isSavingData: true,
    };
  },
  [SAVE_REGSINJECTOR_SUCCESS]: (state) => {
    return {
      ...state,
      isSavingData: false,
    };
  },
  [SAVE_REGSINJECTOR_FAILURE]: (state, action) => {
    return {
      ...state,
      errorCode: action.json,
      isSavingData: false,
      isError: true,
    };
  },
};

// ------------------------------------
// Reducer
// ------------------------------------
// Shape mirrors the other tracker slices (renderData + formDataValue keyed by
// page code). The shared InputField/TextArea write edits into
// formDataValue.RINJ.extractorFields.textValues[<field id>] via actionHandler,
// so that map is pre-seeded here with an empty string per RegsExtractorFields
// property. The StateScape Extractor payload will replace these seeds once
// ingestion is wired.
const seededTextValues = EXTRACTOR_FIELDS.reduce((values, field) => {
  values[field.id] = "";
  return values;
}, {});

const initialState = {
  errorCode: "",
  isFetchingData: false,
  isSavingData: false,
  isMatchingData: false,
  // Reg ids returned by /RegsExtractor/match that may duplicate this extract.
  duplicateIds: [],
  renderData: {},
  formDataValue: {
    RINJ: {
      [EXTRACTOR_FIELD]: {
        textValues: seededTextValues,
        // Dropdown selections (currently just State) live here via the select path.
        radio: {},
        // Selected Team Group numbers (as strings); saved comma-delimited.
        teams: [],
        // Source URL of the extracted page, carried through load -> save.
        url: "",
      },
    },
  },
};

export default function regsInjectorReducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}



// WEBPACK FOOTER //
// ./src/store/reducers/regsInjectorFormData.js